// Match summary shown on the pitch during the football half-time break,
// one card after the other after the "half time" card: team stats (shots,
// corners, ...) and then the top player for a few player stats. A stat with
// nothing to show (missing, or 0 for both teams / every player) gets no card.

import {PLAYER_STAT_COLUMNS, readPlayers, shortPlayerName} from "@/services/matchtracker/playerStatsData";

// Team stats, in order. `types`: feed statistic types, the first present wins;
// `sum`: fallback when none is, the total of these types.
const TEAM_SLIDES = [
    { kind: 'shots', types: [2046], sum: [2, 3, 16], label: 'statistics.totalshots' },
    { kind: 'on-target', types: [2], label: 'statistics.shotsontarget' },
    { kind: 'corners', types: [1], label: 'statistics.corners' },
    { kind: 'possession', types: [11], label: 'statistics.possession', unit: '%' },
    { kind: 'yellow', types: [6], label: 'statistics.yellowcard' }
];

// Player stats that get a "best player" card, in order (PLAYER_STAT_COLUMNS keys).
const PLAYER_SLIDES = [
    { kind: 'shots', key: 'shots' },
    { kind: 'on-target', key: 'ontarget' },
    { kind: 'passes', key: 'passes' },
    { kind: 'saves', key: 'saves' },
    { kind: 'tackles', key: 'tackles' }
];

const toNumber = v => {
    const n = parseFloat(String(v ?? '').replace(',', '.'));
    return isNaN(n) ? 0 : n;
};

// Feed statistics as { type: { h, a } }.
function statsByType(ls) {
    const byType = {};
    (ls?.Statistics || ls?.statistics || []).forEach(s => {
        const res = s.Results || s.results || [];
        const value = pos => {
            const r = res.find(x => String(x.Position ?? x.position) === pos);
            return r ? toNumber(r.Value ?? r.value) : 0;
        };
        byType[parseInt(s.Type ?? s.type)] = { h: value('1'), a: value('2') };
    });
    return byType;
}

// "Parate (Giocatore)" -> "Parate": the card already says whose it is.
const plainLabel = text => String(text).replace(/\s*\([^)]*\)\s*$/, '');

/**
 * @param {Object} opts
 * @param {Object} opts.ls          livescore (Statistics, PlayerStatistics)
 * @param {Object} opts.matchData   event detail (competitors, home_id / away_id)
 * @param {Object} opts.playerStats live-scores/player-stats answer, if any
 * @param {String} opts.homeName
 * @param {String} opts.awayName
 * @param {Function} opts.t         i18n
 * @returns {Array} team cards { id, type: 'team', kind, title, homeName, awayName, home, away, unit, lead }
 *                  and player cards { id, type: 'player', kind, title, player, team, side, value }
 */
export function buildBreakSummary({ ls, matchData, playerStats, homeName, awayName, t }) {
    const slides = [];
    const stats = statsByType(ls);

    TEAM_SLIDES.forEach(def => {
        let stat = def.types.map(type => stats[type]).find(Boolean);
        if (!stat && def.sum && def.sum.some(type => stats[type])) {
            stat = def.sum.reduce((acc, type) => {
                const s = stats[type];
                return s ? { h: acc.h + s.h, a: acc.a + s.a } : acc;
            }, { h: 0, a: 0 });
        }
        if (!stat || (!stat.h && !stat.a)) return;
        slides.push({
            id: 'team-' + def.kind,
            type: 'team',
            kind: def.kind,
            title: plainLabel(t(def.label)),
            homeName,
            awayName,
            home: stat.h,
            away: stat.a,
            unit: def.unit || '',
            lead: stat.h > stat.a ? '1' : stat.a > stat.h ? '2' : null
        });
    });

    const players = readPlayers(playerStats, matchData).filter(p => p.played && p.name.trim());
    PLAYER_SLIDES.forEach(def => {
        // Highest value, then the one who played longer.
        const best = players
            .filter(p => p.stats[def.key] > 0)
            .sort((a, b) => b.stats[def.key] - a.stats[def.key] || b.extras.minutes - a.extras.minutes)[0];
        if (!best) return;
        const side = best.side === 'home' ? '1' : best.side === 'away' ? '2' : '0';
        slides.push({
            id: 'player-' + def.key,
            type: 'player',
            kind: def.kind,
            title: plainLabel(t(PLAYER_STAT_COLUMNS.find(c => c.key === def.key).label)),
            player: shortPlayerName(best.name),
            team: side === '1' ? homeName : side === '2' ? awayName : '',
            side,
            value: best.stats[def.key]
        });
    });

    return slides;
}
